import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { 
  Play, Clock, ArrowLeft, CheckCircle2, Award, 
  Share2, BookOpen, Loader2, AlertCircle, ChevronRight,
  Video, FileQuestion, Trophy
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import ContentFeedback from '../components/ContentFeedback';
const API = `${API_BASE}/api`;

// Navigate Learning Academy branding - EXACT logo
const NLA_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/ckawpz0h_Asset%203.png";

// YouTube video IDs are always 11 URL-safe characters
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const IS_YOUTUBE = /(?:^|\/\/|\.)(?:youtube\.com|youtube-nocookie\.com|youtu\.be)\//i;

// Pull the video ID out of any YouTube URL shape.
// The ID can sit in the path (/embed, /v, /shorts, /live, youtu.be) or in the
// query string, where "v" is not necessarily the first parameter -
// e.g. /watch?app=desktop&v=ID or /watch?feature=shared&v=ID.
function getYouTubeId(url) {
  const pathMatch = url.match(
    /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:embed\/|v\/|shorts\/|live\/))([^?&/#\s]+)/i
  );
  if (pathMatch && YOUTUBE_ID.test(pathMatch[1])) return pathMatch[1];

  if (/youtube(?:-nocookie)?\.com/i.test(url)) {
    const queryMatch = url.match(/[?&]v=([^&#\s]+)/);
    if (queryMatch && YOUTUBE_ID.test(queryMatch[1])) return queryMatch[1];
  }

  return null;
}

// Extract video ID from various embed URLs
function getVideoEmbedUrl(url) {
  if (!url) return null;
  
  // Normalize URL: trim whitespace and add protocol if missing
  let normalizedUrl = url.trim();
  if (!normalizedUrl.match(/^https?:\/\//i)) {
    normalizedUrl = 'https://' + normalizedUrl;
  }
  
  // If it's already an iframe HTML, extract the src
  const iframeMatch = normalizedUrl.match(/src=["']([^"']+)["']/);
  if (iframeMatch) {
    return iframeMatch[1];
  }
  
  // YouTube - various URL formats (handles www.youtu.be, youtu.be, youtube.com, www.youtube.com)
  const youtubeMatch = normalizedUrl.match(/(?:(?:www\.)?youtube\.com\/(?:watch\?v=|embed\/|v\/)|(?:www\.)?youtu\.be\/)([^&?/\s]+)/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo (handles www prefix)
  const vimeoMatch = normalizedUrl.match(/(?:www\.)?vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  // Loom (handles www prefix)
  const loomMatch = normalizedUrl.match(/(?:www\.)?loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) {
    return `https://www.loom.com/embed/${loomMatch[1]}`;
  }
  
  // Microsoft Stream
  const streamMatch = normalizedUrl.match(/(?:www\.)?microsoftstream\.com\/video\/([a-zA-Z0-9-]+)/);
  if (streamMatch) {
    return `https://web.microsoftstream.com/embed/video/${streamMatch[1]}`;
  }
  
  // Wistia
  const wistiaMatch = normalizedUrl.match(/(?:www\.)?wistia\.com\/medias\/([a-zA-Z0-9]+)/);
  if (wistiaMatch) {
    return `https://fast.wistia.net/embed/iframe/${wistiaMatch[1]}`;
  }
  
  // Already an embed URL (contains embed, player, or iframe keywords)
  if (normalizedUrl.includes('embed') || normalizedUrl.includes('player') || normalizedUrl.includes('/e/')) {
    return normalizedUrl;
  }
  
  // Direct video file URLs (mp4, webm, etc.) - return as-is for HTML5 video
  if (normalizedUrl.match(/\.(mp4|webm|ogg|mov)(\?|$)/i)) {
    return normalizedUrl;
  }
  
  // Return normalized URL for other video URLs that might work in iframe
  return normalizedUrl;
}

export default function MasterclassDetailPage() {
  const { webinarId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [masterclass, setMasterclass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [watchProgress, setWatchProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [startingQuiz, setStartingQuiz] = useState(false);

  useEffect(() => {
    fetchMasterclass();
  }, [webinarId]);

  const fetchMasterclass = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/webinars/${webinarId}`);
      setMasterclass(response.data);
      
      // Check if user has completed this masterclass
      if (token) {
        try {
          const progressRes = await axios.get(`${API}/user/webinar-progress/${webinarId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setWatchProgress(progressRes.data.progress || 0);
          setIsCompleted(progressRes.data.completed || false);
        } catch (e) {
          // Progress tracking not implemented yet, ignore
        }
      }
    } catch (err) {
      console.error('Failed to fetch masterclass:', err);
      setError('Masterclass not found');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!token) {
      toast.error('Please log in to track your progress');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/webinars/${webinarId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsCompleted(true);
      setWatchProgress(100);
      toast.success('Masterclass marked as complete! +100 XP');
    } catch (err) {
      console.error('Failed to mark complete:', err);
      toast.error('Failed to save progress. Please try again.');
    }
  };

  const handleStartQuiz = async () => {
    if (!masterclass?.quiz_id) {
      toast.error('No quiz available for this masterclass');
      return;
    }
    
    setStartingQuiz(true);
    try {
      navigate(`/quiz/${masterclass.quiz_id}`);
    } catch (err) {
      toast.error('Failed to start quiz');
    } finally {
      setStartingQuiz(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-[#1EBFB3] animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error || !masterclass) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl text-white mb-2">Masterclass Not Found</h2>
          <p className="text-zinc-400 mb-4">{error || 'The masterclass you requested could not be found.'}</p>
          <Link to="/masterclasses" className="text-[#1EBFB3] hover:underline flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back to Masterclasses
          </Link>
        </div>
      </Layout>
    );
  }

  const embedUrl = getVideoEmbedUrl(masterclass.video_url);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-zinc-400 mb-6">
          <Link to="/masterclasses" className="hover:text-white transition-colors flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Masterclasses
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-white truncate max-w-md">{masterclass.title}</span>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content - Video Player */}
          <div className="lg:col-span-2">
            {/* Video Player */}
            <div className="relative bg-black rounded-2xl overflow-hidden aspect-video mb-6">
              {embedUrl ? (
                // Check if it's a direct video file URL
                embedUrl.match(/\.(mp4|webm|ogg|mov)(\?|$)/i) ? (
                  <video
                    src={embedUrl}
                    title={masterclass.title}
                    className="absolute inset-0 w-full h-full"
                    controls
                    controlsList="nodownload"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <iframe
                    src={embedUrl}
                    title={masterclass.title}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                )
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-900">
                  <Video className="h-16 w-16 text-zinc-600 mb-4" />
                  <p className="text-zinc-400">Video not available</p>
                  {masterclass.video_url && (
                    <a 
                      href={masterclass.video_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-4 text-[#1EBFB3] hover:underline"
                    >
                      Open video in new tab
                    </a>
                  )}
                </div>
              )}
              
              {/* Completion overlay */}
              {isCompleted && (
                <div className="absolute top-4 right-4 px-3 py-1.5 bg-green-500/90 text-white rounded-full text-sm font-medium flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" />
                  Completed
                </div>
              )}
            </div>

            {/* Title & Description */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <span className="inline-block px-3 py-1 bg-[#1EBFB3]/20 text-[#1EBFB3] text-xs font-medium rounded-full mb-3">
                    {masterclass.category}
                  </span>
                  <h1 className="text-2xl md:text-3xl font-bold text-white">
                    {masterclass.title}
                  </h1>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400 mb-6">
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  {masterclass.duration_minutes} minutes
                </span>
                {masterclass.has_quiz && masterclass.quiz_id && (
                  <span className="flex items-center gap-1.5 text-green-400">
                    <FileQuestion className="h-4 w-4" />
                    Quiz Available
                  </span>
                )}
                
                {/* Feedback Component */}
                <ContentFeedback
                  contentType="masterclass"
                  contentId={webinarId}
                  token={token}
                  size="sm"
                  showStats={true}
                />
              </div>

              <div className="prose prose-invert max-w-none">
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
                  {masterclass.description}
                </p>
              </div>
            </div>

            {/* Navigate Learning Academy Branding */}
            <div className="flex items-center justify-center gap-3 py-4 border-t border-zinc-800">
              <img src={NLA_LOGO} alt="Navigate Learning Academy" className="h-8 w-8 object-contain" />
              <span className="text-sm text-zinc-500">Powered by Navigate Learning Academy</span>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Progress Card */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-[#C8A85A]" />
                Your Progress
              </h3>
              
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-400">Watch Progress</span>
                  <span className="text-white font-medium">{watchProgress}%</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#1EBFB3] to-[#C8A85A] transition-all duration-500"
                    style={{ width: `${watchProgress}%` }}
                  />
                </div>
              </div>

              {!isCompleted ? (
                <Button
                  onClick={handleMarkComplete}
                  className="w-full bg-[#1EBFB3] hover:bg-[#25D4C7] text-black font-medium"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark as Complete
                </Button>
              ) : (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                  <CheckCircle2 className="h-6 w-6 text-green-400 mx-auto mb-2" />
                  <p className="text-green-400 font-medium">Completed!</p>
                </div>
              )}
            </div>

            {/* Quiz Card - only show if quiz_id exists */}
            {masterclass.has_quiz && masterclass.quiz_id && (
              <div className="bg-gradient-to-br from-[#1EBFB3]/10 to-[#C8A85A]/10 border border-[#1EBFB3]/30 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <FileQuestion className="h-5 w-5 text-[#1EBFB3]" />
                  Knowledge Check
                </h3>
                <p className="text-sm text-zinc-400 mb-4">
                  Test your understanding with a quiz based on this masterclass.
                </p>
                <Button
                  onClick={handleStartQuiz}
                  disabled={startingQuiz || !isCompleted}
                  className="w-full bg-[#C8A85A] hover:bg-[#D4B76A] text-black font-medium disabled:opacity-50"
                >
                  {startingQuiz ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 mr-2" />
                  )}
                  {isCompleted ? 'Start Quiz' : 'Complete video first'}
                </Button>
              </div>
            )}

            {/* Related Masterclasses */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                More Masterclasses
              </h3>
              <Link
                to="/masterclasses"
                className="flex items-center justify-between p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <span className="text-zinc-300">Browse all masterclasses</span>
                <ChevronRight className="h-4 w-4 text-zinc-400" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
